import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Table from "@/components/ui/table";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import PageHeader from "@/components/ui/page-header";
import InfoCard from "@/components/ui/info-card";
import Chip from "@/components/ui/chip";
import useNotification from "@/hooks/use-notification";
import { itemsApi } from "../services/items.api";
import type { Item, CreateItemRequest } from "../types/items.types";
import type { FilterField, ViewMode } from "@/types/component";
import { Edit, Delete, Tick } from "@/components/icons";
import Header from "@/components/ui/header";
import CatalogCard from "@/components/ui/catalog-card";
import PollCheckbox, { PollCheckboxQuestion } from "@/components/ui/poll-checkbox";
import PollRadio from "@/components/ui/poll-radio";
import PollSelect from "@/components/ui/poll-select";
import PollNumbers from "@/components/ui/poll-numbers";
import PollStar from "@/components/ui/poll-star";
import PollTextarea from "@/components/ui/poll-textarea";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";

const ItemsPage: React.FC = () => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotification();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.ITEMS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.ITEMS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.ITEMS_DELETE);

  // State
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  // Form state
  const [formData, setFormData] = useState<CreateItemRequest>({
    name: "",
    description: "",
    price: 0,
    quantity: 0,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Poll checkbox demo state (test purposes)
  const [pollSelections, setPollSelections] = useState<Record<string, boolean>>({
    "sunum-grafik": false,
    "demo-videolar": false,
    "teknik-el-kitabi": false,
    "soru-cevap": false,
  });
  // Poll radio demo state (test purposes)
  const [pollRadioValue, setPollRadioValue] = useState<string>("");
  // Poll star demo state (test purposes)
  const [pollStarRating, setPollStarRating] = useState(0);
  // Poll select demo state (test purposes)
  const [pollSelectValue, setPollSelectValue] = useState<string | null>(null);
  // Poll textarea demo state (test purposes)
  const [pollTextareaValue, setPollTextareaValue] = useState("");
  // Poll numbers demo state (test purposes)
  const [pollNumbersValue, setPollNumbersValue] = useState(0);

  // Load items
  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await itemsApi.getAll({
        page: currentPage,
        pageSize,
        search: searchTerm,
        ...filterValues, // Include filter values in the request
      });
      setItems(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      notifyError(t("messages.error.loadFailed"));
      console.error("Error loading items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load items when page, pageSize, or filters change
  useEffect(() => {
    loadItems();
  }, [currentPage, pageSize, filterValues]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadItems();
      } else {
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle create/edit
  const handleOpenModal = (item?: Item) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price,
        quantity: item.quantity,
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: "",
        description: "",
        price: 0,
        quantity: 0,
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({
      name: "",
      description: "",
      price: 0,
      quantity: 0,
    });
    setFormErrors({});
  };

  // Form validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = t("validation.required");
    }

    if (!formData.description.trim()) {
      errors.description = t("validation.required");
    }

    if (formData.price <= 0) {
      errors.price = t("validation.required");
    }

    if (formData.quantity < 0) {
      errors.quantity = t("validation.required");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingItem) {
        await itemsApi.update(editingItem.id, formData);
        notifySuccess(t("items.updated"));
      } else {
        await itemsApi.create(formData);
        notifySuccess(t("items.created"));
      }
      handleCloseModal();
      loadItems();
    } catch (error) {
      notifyError(
        editingItem
          ? t("messages.error.updateFailed")
          : t("messages.error.createFailed")
      );
      console.error("Error saving item:", error);
    }
  };

  // Handle delete
  const handleDelete = async (item: Item) => {
    if (!window.confirm(t("items.deleteConfirm"))) {
      return;
    }

    try {
      await itemsApi.delete(item.id);
      notifySuccess(t("items.deleted"));
      loadItems();
    } catch (error) {
      notifyError(t("messages.error.deleteFailed"));
      console.error("Error deleting item:", error);
    }
  };

  // Handle filter change
  const handleFilterChange = (filters: Record<string, any>) => {
    console.log("=== Filter Values Applied ===");
    console.log("Filters:", filters);
    console.log("Filter Keys:", Object.keys(filters));
    console.log(
      "Active Filters:",
      Object.entries(filters).filter(([_, value]) => value)
    );
    console.log("============================");

    setFilterValues(filters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Define filter fields for the PageHeader
  const filterFields: FilterField[] = [
    {
      name: "category",
      label: t("common.category"),
      type: "dropdown",
      placeholder: t("common.selectCategory"),
      options: [
        { label: "Electronics", value: "electronics" },
        { label: "Books", value: "books" },
        { label: "Clothing", value: "clothing" },
        { label: "Furniture", value: "furniture" },
        { label: "Toys", value: "toys" },
      ],
      searchable: true,
    },
    {
      name: "priceRange",
      label: t("common.priceRange"),
      type: "dropdown",
      placeholder: t("common.selectPriceRange"),
      options: [
        { label: "Under $10", value: "0-10" },
        { label: "$10 - $50", value: "10-50" },
        { label: "$50 - $100", value: "50-100" },
        { label: "Over $100", value: "100+" },
      ],
    },
    {
      name: "status",
      label: t("common.status"),
      type: "dropdown",
      placeholder: t("common.selectStatus"),
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "Out of Stock", value: "out_of_stock" },
        { label: "Discontinued", value: "discontinued" },
      ],
    },
    {
      name: "minQuantity",
      label: t("common.minQuantity"),
      type: "number",
      placeholder: t("common.enterMinQuantity"),
    },
    {
      name: "createdFrom",
      label: t("common.createdFrom"),
      type: "date",
      placeholder: t("common.selectDate"),
    },
    {
      name: "createdTo",
      label: t("common.createdTo"),
      type: "date",
      placeholder: t("common.selectDate"),
    },
    {
      name: "searchTag",
      label: t("common.tag"),
      type: "text",
      placeholder: t("common.enterTag"),
    },
    {
      name: "sku",
      label: t("common.sku"),
      type: "text",
      placeholder: t("common.enterSku"),
    },
  ];

  // Table columns
  const columns = [
    {
      header: t("common.name"),
      accessor: (item: Item) => item.name,
    },
    {
      header: t("common.description"),
      accessor: (item: Item) => item.description,
    },
    {
      header: t("common.price"),
      accessor: (item: Item) => `$${item.price.toFixed(2)}`,
    },
    {
      header: t("common.quantity"),
      accessor: (item: Item) => item.quantity,
    },
    {
      header: t("common.createdAt"),
      accessor: (item: Item) => new Date(item.createdAt).toLocaleDateString(),
    },
  ];

  // Table actions
  const renderActions = (item: Item) => {
    return (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        {canUpdate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal(item);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            title={t("buttons.edit")}
          >
            <Edit width={20} height={20} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(item);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              color: "#FB3748",
            }}
            title={t("buttons.delete")}
          >
            <Delete width={20} height={20} />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <DashboardLayout>
        <Header title={t("items.title")} description={t("items.description")} />

        <PageHeader
          searchPlaceholder={t("common.search")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          onAddNew={canCreate ? () => handleOpenModal() : undefined}
          addButtonLabel={canCreate ? t("buttons.addItem") : undefined}
          showViewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* Info Cards Demo */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <InfoCard
            title={t("itemsPage.completedTrainings")}
            value="14"
            icon={<Tick width={24} height={24} />}
          />
          <InfoCard
            title="Total Items"
            value={totalRecords}
            icon={<Edit width={24} height={24} />}
            iconBgColor="#22c55e"
          />
          <InfoCard
            title="Active Items"
            value={items.length}
            icon={<Tick width={20} height={20} />}
            iconBgColor="#00b8d9"
          />
          <InfoCard
            title="Active Items"
            value={items.length}
            icon={<Tick width={20} height={20} />}
            iconBgColor="#00b8d9"
          />
        </div>

        {/* Chip Component Demo */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Chip Examples
          </h3>

          {/* Different Types - Filled */}
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Filled Variants:
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Chip type="primary" variant="filled">
                Primary
              </Chip>
              <Chip type="success" variant="filled">
                Success
              </Chip>
              <Chip type="error" variant="filled">
                Error
              </Chip>
              <Chip type="warning" variant="filled">
                Warning
              </Chip>
              <Chip type="info" variant="filled">
                Info
              </Chip>
              <Chip type="default" variant="filled">
                Default
              </Chip>
            </div>
          </div>

          {/* Different Types - Outlined */}
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Outlined Variants:
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Chip type="primary" variant="outlined">
                Primary
              </Chip>
              <Chip type="success" variant="outlined">
                Success
              </Chip>
              <Chip type="error" variant="outlined">
                Error
              </Chip>
              <Chip type="warning" variant="outlined">
                Warning
              </Chip>
              <Chip type="info" variant="outlined">
                Info
              </Chip>
            </div>
          </div>

          {/* Different Types - Soft */}
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Soft Variants:
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Chip type="primary" variant="soft">
                Primary
              </Chip>
              <Chip type="success" variant="soft">
                Success
              </Chip>
              <Chip type="error" variant="soft">
                Error
              </Chip>
              <Chip type="warning" variant="soft">
                Warning
              </Chip>
              <Chip type="info" variant="soft">
                Info
              </Chip>
            </div>
          </div>

          {/* Different Sizes */}
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Sizes:
            </p>
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Chip type="primary" size="small">
                Small
              </Chip>
              <Chip type="primary" size="medium">
                Medium
              </Chip>
            </div>
          </div>

          {/* With Delete/Click */}
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Clickable (with onDelete):
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Chip
                type="primary"
                variant="filled"
                onDelete={() => console.log("Electronics deleted")}
              >
                Electronics
              </Chip>
              <Chip
                type="success"
                variant="soft"
                onDelete={() => console.log("Books deleted")}
              >
                Books
              </Chip>
              <Chip
                type="warning"
                variant="outlined"
                onDelete={() => console.log("Sale deleted")}
              >
                Sale
              </Chip>
            </div>
          </div>

          {/* Status Examples */}
          <div>
            <p
              style={{
                fontSize: "14px",
                color: "#72808f",
                marginBottom: "8px",
              }}
            >
              Status Examples:
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Chip type="success" variant="soft">
                Active
              </Chip>
              <Chip type="warning" variant="soft">
                Pending
              </Chip>
              <Chip type="error" variant="soft">
                Out of Stock
              </Chip>
              <Chip type="info" variant="soft">
                In Transit
              </Chip>
              <Chip type="primary" variant="soft">
                Featured
              </Chip>
              <Chip type="default" variant="soft">
                Draft
              </Chip>
            </div>
          </div>
        </div>

        {/* Poll Checkbox (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Checkbox (test)
          </h3>
          <PollCheckboxQuestion hint={t("itemsPage.demo.multiSelectHint")}>
            2. Eğitim sırasında kullanılan hangi materyalleri öğrenme sürecinizde
            en faydalı buldunuz?
          </PollCheckboxQuestion>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <PollCheckbox
              label={t("itemsPage.demo.presentationFiles")}
              value="sunum-grafik"
              checked={pollSelections["sunum-grafik"]}
              onChange={(checked) =>
                setPollSelections((prev) => ({
                  ...prev,
                  "sunum-grafik": checked,
                }))
              }
            />
            <PollCheckbox
              label={t("itemsPage.demo.demoVideos")}
              value="demo-videolar"
              checked={pollSelections["demo-videolar"]}
              onChange={(checked) =>
                setPollSelections((prev) => ({
                  ...prev,
                  "demo-videolar": checked,
                }))
              }
            />
            <PollCheckbox
              label={t("itemsPage.demo.technicalManual")}
              value="teknik-el-kitabi"
              checked={pollSelections["teknik-el-kitabi"]}
              onChange={(checked) =>
                setPollSelections((prev) => ({
                  ...prev,
                  "teknik-el-kitabi": checked,
                }))
              }
            />
            <PollCheckbox
              label={t("itemsPage.demo.qaSessions")}
              value="soru-cevap"
              checked={pollSelections["soru-cevap"]}
              onChange={(checked) =>
                setPollSelections((prev) => ({
                  ...prev,
                  "soru-cevap": checked,
                }))
              }
            />
          </div>
        </div>

        {/* Poll Radio (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Radio (test)
          </h3>
          <PollCheckboxQuestion>
            1. Eğitimin süresi, içeriğin kapsamı ve anlaşılırlığı için sizce
            yeterli miydi?
          </PollCheckboxQuestion>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <PollRadio
              name="poll-duration"
              label={t("itemsPage.demo.veryShort")}
              value="cok-kisa"
              checked={pollRadioValue === "cok-kisa"}
              onChange={setPollRadioValue}
            />
            <PollRadio
              name="poll-duration"
              label="Yeterli"
              value="yeterli"
              checked={pollRadioValue === "yeterli"}
              onChange={setPollRadioValue}
            />
            <PollRadio
              name="poll-duration"
              label={t("itemsPage.demo.veryLong")}
              value="cok-uzun"
              checked={pollRadioValue === "cok-uzun"}
              onChange={setPollRadioValue}
            />
          </div>
        </div>

        {/* Poll Star (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Star (test)
          </h3>
          <PollCheckboxQuestion>
            3. Eğitimin genel kalitesini ve içeriğini 5 üzerinden puanlayınız.
          </PollCheckboxQuestion>
          <PollStar
            value={pollStarRating}
            onChange={setPollStarRating}
            maxStars={5}
          />
        </div>

        {/* Poll Select (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Select (test)
          </h3>
          <PollCheckboxQuestion>
            4. Bu teknik eğitimin hangi aralıklarla tekrarlanması gerektiğini
            düşünüyorsunuz?
          </PollCheckboxQuestion>
          <PollSelect
            placeholder={t("itemsPage.demo.select")}
            options={[
              { label: "Haftada bir", value: "haftada-bir" },
              { label: "Ayda bir", value: "ayda-bir" },
              { label: "Üç ayda bir", value: "uc-ayda-bir" },
              { label: "Yılda bir", value: "yilda-bir" },
            ]}
            value={pollSelectValue}
            onChange={(v) => setPollSelectValue(v)}
          />
        </div>

        {/* Poll Textarea (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Textarea (test)
          </h3>
          <PollCheckboxQuestion>
            5. Eğitim hakkındaki görüşleriniz nelerdir?
          </PollCheckboxQuestion>
          <PollTextarea
            placeholder={t("itemsPage.demo.opinionsPlaceholder")}
            value={pollTextareaValue}
            onChange={(e) => setPollTextareaValue(e.target.value)}
          />
        </div>

        {/* Poll Numbers (test) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Poll Numbers (test)
          </h3>
          <PollCheckboxQuestion>
            5. Eğitmenin eğitim yöntemini puanlayınız.
          </PollCheckboxQuestion>
          <PollNumbers
            value={pollNumbersValue}
            onChange={setPollNumbersValue}
            min={1}
            max={5}
            labelMin={t("itemsPage.demo.worst")}
            labelMax="En iyi"
          />
        </div>

        {/* Catalog Card Examples */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e9",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}
          >
            Catalog Card Examples
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            {/* Simple Status - Talep Toplanıyor */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training1")}
              educatorName="Ayşe Yılmaz"
              duration="2 saat"
              deliveryType="Yüz Yüze"
              category="Teknik"
              statusType="simple"
              statusText={t("itemsPage.demo.statusCollecting")}
              buttonLabel={t("itemsPage.demo.requestCreate")}
              onButtonClick={() => console.log("Talep Oluştur clicked")}
            />

            {/* Deadline Status */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training1")}
              educatorName="Ayşe Yılmaz"
              duration="2 saat"
              deliveryType="Üniteli"
              category={t("itemsPage.demo.healthSafety")}
              statusType="deadline"
              deadline="20 Nisan 2026"
              buttonLabel={t("itemsPage.demo.requestCreate")}
              onButtonClick={() => console.log("Deadline card clicked")}
            />

            {/* Participant Status with DateTime */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training1")}
              educatorName="Ayşe Yılmaz"
              duration="1 Saat"
              deliveryType="Canlı"
              category={t("itemsPage.demo.administrativeSafety")}
              statusType="participant"
              participantCount={12}
              maxParticipants={200}
              dateTime="20 Nisan 2026, 12:30 - 13:30"
              buttonLabel={t("itemsPage.demo.requestCreate")}
              onButtonClick={() => console.log("Participant card clicked")}
            />

            {/* Another Deadline variant */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training1")}
              educatorName="Ayşe Yılmaz"
              duration="2 saat"
              deliveryType="Üniteli"
              category="Teknik"
              statusType="deadline"
              deadline="20 Nisan 2026"
              buttonLabel={t("itemsPage.demo.requestCreate")}
              onButtonClick={() => console.log("Another deadline clicked")}
            />

            {/* Online variant */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training2")}
              educatorName="Mehmet Demir"
              duration="3 saat"
              deliveryType="Online"
              category="Teknik"
              statusType="simple"
              statusText={t("itemsPage.demo.statusEnrollOpen")}
              buttonLabel={t("itemsPage.demo.enroll")}
              onButtonClick={() => console.log("Online card clicked")}
            />

            {/* Hybrid variant */}
            <CatalogCard
              image="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400"
              title={t("itemsPage.demo.training3")}
              educatorName="Fatma Kaya"
              duration="4 saat"
              deliveryType="Hibrit"
              category={t("itemsPage.demo.healthSafety")}
              statusType="participant"
              participantCount={45}
              maxParticipants={100}
              dateTime="25 Nisan 2026, 09:00 - 13:00"
              buttonLabel={t("itemsPage.demo.join")}
              onButtonClick={() => console.log("Hybrid card clicked")}
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={items}
          isLoading={loading}
          renderActions={renderActions}
          getRowId={(item) => item.id}
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          emptyMessage={t("common.noData")}
        />
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={editingItem ? t("items.edit") : t("items.create")}
          withForm
          formId="itemForm"
          handleSubmit={handleSubmit}
          submitButtonText={
            editingItem ? t("buttons.update") : t("buttons.create")
          }
        >
          <form id="itemForm" onSubmit={(e) => e.preventDefault()}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <Input
                label={t("common.name")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                error={!!formErrors.name}
                errorMessage={formErrors.name}
                required
              />

              <Textarea
                label={t("common.description")}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                error={!!formErrors.description}
                required
                rows={4}
              />

              <Input
                label={t("common.price")}
                type="number"
                value={formData.price.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: parseFloat(e.target.value) || 0,
                  })
                }
                error={!!formErrors.price}
                errorMessage={formErrors.price}
                required
                min="0"
                step="0.01"
              />

              <Input
                label={t("common.quantity")}
                type="number"
                value={formData.quantity.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 0,
                  })
                }
                error={!!formErrors.quantity}
                errorMessage={formErrors.quantity}
                required
                min="0"
              />
            </div>
          </form>
        </Modal>
      </DashboardLayout>
    </>
  );
};

export default ItemsPage;
